import React, { useMemo } from 'react';
import {
	Box,
	Typography,
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	CircularProgress,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { fetchAllShifts } from 'core/API/shifts';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import moment from 'moment-timezone';
import { ShiftRequest } from 'types/shift-requests';
import {
	getPickupRequestFormValues,
	mergePickupShiftOptions,
} from '../../../utils/pickupRequestForm';

interface PickupShiftFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	currentWeekStart?: Date;
	currentWeekEnd?: Date;
	formRef?: React.MutableRefObject<any>;
	editRequest?: ShiftRequest | null;
}

const validationSchema = Yup.object({
	shiftId: Yup.string().required('Please select a shift to pick up'),
	notes: Yup.string(),
});

const formatPickupShiftLabel = (shift: any) => {
	const start = shift.startAt || shift.start_at || shift.start;
	const end = shift.endAt || shift.end_at || shift.end;
	const startLabel = start ? moment(start).format('ddd, MMM D - h:mm A') : 'Shift';
	const endLabel = end ? moment(end).format('h:mm A') : '';
	const jobLabel = shift.jobCode?.label || 'Shift';
	return endLabel ? `${startLabel} - ${endLabel} | ${jobLabel}` : `${startLabel} | ${jobLabel}`;
};

const PickupShiftForm: React.FC<PickupShiftFormProps> = ({
	onSubmit,
	onCancel: _onCancel,
	currentWeekStart,
	currentWeekEnd,
	formRef,
	editRequest,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const selectedShiftId = getPickupRequestFormValues(editRequest).shiftId;

	const { data: houseShiftsData, isLoading: houseShiftsLoading } = useQuery(
		['house-shifts', workplaceId, currentWeekStart, currentWeekEnd],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate: currentWeekStart ? moment(currentWeekStart).format('YYYY-MM-DD') : undefined,
				toDate: currentWeekEnd ? moment(currentWeekEnd).add(1, 'day').format('YYYY-MM-DD') : undefined,
			}),
		{
			enabled: !!workplaceId && !!currentWeekStart && !!currentWeekEnd,
			staleTime: 0,
			cacheTime: 0,
		}
	);

	const houseShifts = useMemo(() => {
		if (!houseShiftsData?.member) return [];
		const weekStart = moment(currentWeekStart).startOf('day');
		const weekEnd = moment(currentWeekEnd).endOf('day');
		const now = moment();

		const filtered = houseShiftsData.member.filter((shift: any) => {
			if (selectedShiftId && String(shift.id) === selectedShiftId) return true;

			const shiftStart = moment(shift.startAt || shift.start);
			const shiftEnd = moment(shift.endAt || shift.end);
			return (
				(!shift.employee || shift.employee === null || shift.employee === undefined) &&
				shiftStart.isBetween(weekStart, weekEnd, null, '[]') &&
				shift.published === true &&
				shiftEnd.isAfter(now)
			);
		});

		return filtered.sort((a: any, b: any) => {
			const startComparison = moment(a.startAt || a.start).diff(moment(b.startAt || b.start));
			if (startComparison !== 0) return startComparison;
			return (a.jobCode?.label || '').localeCompare(b.jobCode?.label || '');
		});
	}, [houseShiftsData, currentWeekStart, currentWeekEnd, selectedShiftId]);

	const pickupShiftOptions = useMemo(() => {
		const selectedFromWeek = houseShifts.find((shift: any) => String(shift.id) === selectedShiftId);
		return mergePickupShiftOptions(
			houseShifts,
			selectedFromWeek || editRequest?.shiftRequest,
			selectedShiftId
		);
	}, [houseShifts, editRequest?.shiftRequest, selectedShiftId]);

	const initialValues = useMemo(
		() => getPickupRequestFormValues(editRequest),
		[editRequest]
	);

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			enableReinitialize
			validationSchema={validationSchema}
			onSubmit={onSubmit}
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<Box sx={{ mt: 3 }}>
						<Typography variant="h6" gutterBottom>
							{editRequest ? 'Edit Pickup Request' : 'Pick Up a Shift'}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Week: {currentWeekStart && moment(currentWeekStart).format('MMM D')} -{' '}
							{currentWeekEnd && moment(currentWeekEnd).format('MMM D, YYYY')}
						</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControl fullWidth error={touched.shiftId && !!errors.shiftId}>
									<InputLabel>Select House Shift *</InputLabel>
									<Select
										value={values.shiftId}
										onChange={(e) => setFieldValue('shiftId', String(e.target.value))}
										label="Select House Shift *"
									>
										{houseShiftsLoading && pickupShiftOptions.length === 0 ? (
											<MenuItem value="" disabled>
												<CircularProgress size={20} color="inherit" /> Loading shifts...
											</MenuItem>
										) : pickupShiftOptions.length === 0 ? (
											<MenuItem value="" disabled>
												No house shifts available this week
											</MenuItem>
										) : (
											pickupShiftOptions.map((shift: any) => (
												<MenuItem key={shift.id} value={String(shift.id)}>
													{formatPickupShiftLabel(shift)}
												</MenuItem>
											))
										)}
									</Select>
									{touched.shiftId && errors.shiftId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
											{String(errors.shiftId)}
										</Typography>
									)}
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									multiline
									rows={3}
									label="Notes (Optional)"
									value={values.notes}
									onChange={(e) => setFieldValue('notes', e.target.value)}
									placeholder="Any additional notes about picking up this shift"
								/>
							</Grid>
						</Grid>
					</Box>
				</Form>
			)}
		</Formik>
	);
};

export default PickupShiftForm;
